import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import api, { setAuthToken } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { SignIn, SignOut, Spinner, Eye, Trash, UserPlus, FilePlus, ShieldCheck } from "@phosphor-icons/react";

const interestLabels = {
  "technical-overview": "Technical Overview",
  "architecture-briefing": "Architecture Briefing",
  "integration-discussion": "Integration / Pilot",
  "investor-inquiry": "Investor Inquiry",
  "partnership": "Partnership",
  "engineering-interest": "Engineering / Careers",
  "other": "Other",
};

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) setAuthToken(data.token);
      onLogin(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center">
      <motion.div initial="hidden" animate="visible" variants={fadeUp} className="w-full max-w-sm px-6">
        <div className="bg-[#121519] border border-[#2d3239] rounded-none p-8">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-2">Admin</p>
          <h2 className="text-xl font-bold text-[#f2f4f8] mb-6">Internal Portal</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="admin-email" className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Email</Label>
              <Input
                id="admin-email"
                type="email"
                data-testid="admin-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus-visible:ring-1 focus-visible:ring-[#4b7399]"
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Password</Label>
              <Input
                id="admin-password"
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none focus-visible:ring-1 focus-visible:ring-[#4b7399]"
              />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-[#d97706] font-mono">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-6 py-3 rounded-none hover:bg-[#3a5a78] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Spinner size={16} className="animate-spin" /> : <SignIn size={16} />}
              {loading ? "Authenticating..." : "Access Portal"}
            </button>
          </form>
        </div>
      </motion.div>
    </main>
  );
}

function SubmissionsTab({ submissions, onMarkReviewed }) {
  if (submissions.length === 0) {
    return <p className="text-[#8c9eb3] text-center py-12">No submissions yet.</p>;
  }
  return (
    <div className="border border-[#2d3239] rounded-none overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#2d3239] hover:bg-transparent">
            <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Date</TableHead>
            <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Name</TableHead>
            <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Company</TableHead>
            <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Email</TableHead>
            <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Interest</TableHead>
            <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Message</TableHead>
            <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Status</TableHead>
            <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissions.map((sub) => (
            <TableRow key={sub.id} className="border-[#2d3239] hover:bg-[#1a1e24]">
              <TableCell className="font-mono text-xs text-[#8c9eb3]">{new Date(sub.created_at).toLocaleDateString()}</TableCell>
              <TableCell className="text-sm text-[#f2f4f8]">{sub.name}</TableCell>
              <TableCell className="text-sm text-[#8c9eb3]">{sub.company}</TableCell>
              <TableCell className="text-sm text-[#8c9eb3]">{sub.email}</TableCell>
              <TableCell className="font-mono text-xs text-[#8c9eb3]">{interestLabels[sub.interest_type] || sub.interest_type}</TableCell>
              <TableCell className="text-sm text-[#8c9eb3] max-w-[200px] truncate">{sub.message}</TableCell>
              <TableCell>
                <Badge
                  className={`rounded-none font-mono text-xs ${sub.status === "reviewed" ? "bg-[#4b7399]/20 text-[#4b7399] border-[#4b7399]" : "bg-[#d97706]/20 text-[#d97706] border-[#d97706]"}`}
                  variant="outline"
                >{sub.status}</Badge>
              </TableCell>
              <TableCell>
                {sub.status === "new" && (
                  <button onClick={() => onMarkReviewed(sub.id)} className="font-mono text-xs text-[#4b7399] hover:text-[#f2f4f8] transition-colors flex items-center gap-1">
                    <Eye size={14} /> Review
                  </button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function InvestorsTab() {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { fetchInvestors(); }, []);

  const fetchInvestors = async () => {
    try {
      const { data } = await api.get("/admin/investors");
      setInvestors(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createInvestor = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.password) { setError("All fields required."); return; }
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/investors", form);
      setForm({ name: "", email: "", company: "", password: "" });
      setShowForm(false);
      fetchInvestors();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create investor.");
    } finally { setCreating(false); }
  };

  const deleteInvestor = async (id) => {
    if (!window.confirm("Remove this investor's access?")) return;
    try {
      await api.delete(`/admin/investors/${id}`);
      setInvestors(investors.filter(i => i.id !== id));
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner size={24} className="text-[#4b7399] animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8c9eb3]">{investors.length} investor account{investors.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-investor-button"
          className="bg-[#4b7399] text-[#f2f4f8] font-mono text-xs tracking-wider uppercase px-4 py-2 rounded-none hover:bg-[#3a5a78] transition-colors flex items-center gap-2"
        >
          <UserPlus size={14} /> Add Investor
        </button>
      </div>

      {showForm && (
        <form onSubmit={createInvestor} className="bg-[#121519] border border-[#2d3239] rounded-none p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Full name" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none" />
            </div>
            <div>
              <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="investor@firm.com" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none" />
            </div>
            <div>
              <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Company</Label>
              <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Investment firm" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none" />
            </div>
            <div>
              <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder="Temporary password" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none" />
            </div>
          </div>
          {error && <p className="text-sm text-[#d97706] font-mono">{error}</p>}
          <button type="submit" disabled={creating} className="bg-[#4b7399] text-[#f2f4f8] font-mono text-xs tracking-wider uppercase px-4 py-2 rounded-none hover:bg-[#3a5a78] transition-colors disabled:opacity-50">
            {creating ? "Creating..." : "Create Account"}
          </button>
        </form>
      )}

      {investors.length > 0 && (
        <div className="border border-[#2d3239] rounded-none overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#2d3239] hover:bg-transparent">
                <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Name</TableHead>
                <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Email</TableHead>
                <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Company</TableHead>
                <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Created</TableHead>
                <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {investors.map((inv) => (
                <TableRow key={inv.id} className="border-[#2d3239] hover:bg-[#1a1e24]">
                  <TableCell className="text-sm text-[#f2f4f8]">{inv.name}</TableCell>
                  <TableCell className="text-sm text-[#8c9eb3]">{inv.email}</TableCell>
                  <TableCell className="text-sm text-[#8c9eb3]">{inv.company}</TableCell>
                  <TableCell className="font-mono text-xs text-[#8c9eb3]">{new Date(inv.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <button onClick={() => deleteInvestor(inv.id)} className="font-mono text-xs text-[#d97706] hover:text-[#f2f4f8] transition-colors flex items-center gap-1">
                      <Trash size={14} /> Remove
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function DocumentsTab() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "general", url: "" });
  const [creating, setCreating] = useState(false);

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try {
      const { data } = await api.get("/admin/documents");
      setDocuments(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createDoc = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) return;
    setCreating(true);
    try {
      await api.post("/admin/documents", form);
      setForm({ title: "", description: "", category: "general", url: "" });
      setShowForm(false);
      fetchDocs();
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      setDocuments(documents.filter(d => d.id !== id));
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex justify-center py-12"><Spinner size={24} className="text-[#4b7399] animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#8c9eb3]">{documents.length} document{documents.length !== 1 ? 's' : ''} in investor portal</p>
        <button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-document-button"
          className="bg-[#4b7399] text-[#f2f4f8] font-mono text-xs tracking-wider uppercase px-4 py-2 rounded-none hover:bg-[#3a5a78] transition-colors flex items-center gap-2"
        >
          <FilePlus size={14} /> Add Document
        </button>
      </div>

      {showForm && (
        <form onSubmit={createDoc} className="bg-[#121519] border border-[#2d3239] rounded-none p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Document title" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none" />
            </div>
            <div>
              <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Category</Label>
              <Select value={form.category} onValueChange={(val) => setForm({...form, category: val})}>
                <SelectTrigger className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121519] border-[#2d3239] rounded-none">
                  <SelectItem value="general" className="text-[#f2f4f8] rounded-none">General</SelectItem>
                  <SelectItem value="technical" className="text-[#f2f4f8] rounded-none">Technical</SelectItem>
                  <SelectItem value="financial" className="text-[#f2f4f8] rounded-none">Financial</SelectItem>
                  <SelectItem value="legal" className="text-[#f2f4f8] rounded-none">Legal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Brief description" rows={2} className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none resize-none" />
          </div>
          <div>
            <Label className="text-sm font-mono text-[#8c9eb3] uppercase tracking-wide mb-2 block">Document URL (optional)</Label>
            <Input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} placeholder="https://... or leave empty for generated docs" className="bg-[#0a0b0d] border-[#2d3239] text-[#f2f4f8] rounded-none" />
          </div>
          <button type="submit" disabled={creating} className="bg-[#4b7399] text-[#f2f4f8] font-mono text-xs tracking-wider uppercase px-4 py-2 rounded-none hover:bg-[#3a5a78] transition-colors disabled:opacity-50">
            {creating ? "Adding..." : "Add Document"}
          </button>
        </form>
      )}

      {documents.length > 0 && (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div key={doc.id} className="bg-[#121519] border border-[#2d3239] rounded-none p-5 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h4 className="text-sm font-semibold text-[#f2f4f8]">{doc.title}</h4>
                  <Badge className="rounded-none font-mono text-[10px] bg-[#4b7399]/20 text-[#4b7399] border-[#4b7399]" variant="outline">{doc.category}</Badge>
                </div>
                <p className="text-xs text-[#8c9eb3]">{doc.description}</p>
                {doc.url && <p className="text-xs text-[#4b7399] mt-1 font-mono truncate max-w-md">{doc.url}</p>}
              </div>
              <button onClick={() => deleteDoc(doc.id)} className="text-[#d97706] hover:text-[#f2f4f8] transition-colors flex-shrink-0">
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NDAAcceptancesTab() {
  const [acceptances, setAcceptances] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/nda-acceptances")
      .then(({ data }) => setAcceptances(data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Spinner size={24} className="text-[#4b7399] animate-spin" /></div>;

  if (acceptances.length === 0) return <p className="text-[#8c9eb3] text-center py-12">No NDA acceptances recorded yet.</p>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-[#8c9eb3]">{acceptances.length} NDA acceptance{acceptances.length !== 1 ? 's' : ''} recorded</p>
      <div className="border border-[#2d3239] rounded-none overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#2d3239] hover:bg-transparent">
              <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Date / Time</TableHead>
              <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Name</TableHead>
              <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Email</TableHead>
              <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Company</TableHead>
              <TableHead className="text-[#4b7399] font-mono text-xs uppercase">IP Address</TableHead>
              <TableHead className="text-[#4b7399] font-mono text-xs uppercase">NDA Version</TableHead>
              <TableHead className="text-[#4b7399] font-mono text-xs uppercase">Browser</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {acceptances.map((a) => (
              <TableRow key={a.id} className="border-[#2d3239] hover:bg-[#1a1e24]">
                <TableCell className="font-mono text-xs text-[#8c9eb3]">{new Date(a.accepted_at).toLocaleString()}</TableCell>
                <TableCell className="text-sm text-[#f2f4f8]">{a.user_name}</TableCell>
                <TableCell className="text-sm text-[#8c9eb3]">{a.user_email}</TableCell>
                <TableCell className="text-sm text-[#8c9eb3]">{a.company}</TableCell>
                <TableCell className="font-mono text-xs text-[#8c9eb3]">{a.ip_address}</TableCell>
                <TableCell><Badge className="rounded-none font-mono text-[10px] bg-[#4b7399]/20 text-[#4b7399] border-[#4b7399]" variant="outline">v{a.nda_version}</Badge></TableCell>
                <TableCell className="font-mono text-[10px] text-[#8c9eb3] max-w-[200px] truncate">{a.user_agent}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => { setUser(data); setChecking(false); })
      .catch(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) fetchSubmissions();
  }, [user]);

  const fetchSubmissions = async () => {
    setLoadingSubs(true);
    try {
      const { data } = await api.get("/admin/submissions");
      setSubmissions(data);
    } catch (err) { console.error(err); }
    finally { setLoadingSubs(false); }
  };

  const markReviewed = async (id) => {
    try {
      await api.patch(`/admin/submissions/${id}`);
      setSubmissions(submissions.map((s) => s.id === id ? { ...s, status: "reviewed" } : s));
    } catch (err) { console.error(err); }
  };

  const handleLogout = async () => {
    await api.post("/auth/logout");
    setAuthToken(null);
    setUser(null);
  };

  if (checking) return <main className="pt-16 min-h-screen flex items-center justify-center"><Spinner size={32} className="text-[#4b7399] animate-spin" /></main>;
  if (!user) return <AdminLogin onLogin={setUser} />;

  return (
    <main className="pt-16">
      <section data-testid="admin-submissions-section" className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-2">Internal Portal</p>
              <h1 className="text-2xl md:text-3xl font-bold text-[#f2f4f8]">Admin Dashboard</h1>
            </div>
            <button onClick={handleLogout} data-testid="admin-logout-button" className="font-mono text-xs tracking-wider uppercase text-[#8c9eb3] hover:text-[#f2f4f8] transition-colors flex items-center gap-2">
              <SignOut size={16} /> Logout
            </button>
          </div>

          <Tabs defaultValue="submissions" className="w-full">
            <TabsList className="bg-[#121519] border border-[#2d3239] rounded-none p-1 mb-8">
              <TabsTrigger value="submissions" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-[#4b7399] data-[state=active]:text-[#f2f4f8]">
                Submissions ({submissions.length})
              </TabsTrigger>
              <TabsTrigger value="investors" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-[#4b7399] data-[state=active]:text-[#f2f4f8]">
                Investors
              </TabsTrigger>
              <TabsTrigger value="documents" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-[#4b7399] data-[state=active]:text-[#f2f4f8]">
                Documents
              </TabsTrigger>
              <TabsTrigger value="nda" className="rounded-none font-mono text-xs uppercase tracking-wider data-[state=active]:bg-[#4b7399] data-[state=active]:text-[#f2f4f8]">
                NDA Log
              </TabsTrigger>
            </TabsList>

            <TabsContent value="submissions">
              {loadingSubs ? <div className="flex justify-center py-12"><Spinner size={24} className="text-[#4b7399] animate-spin" /></div> : <SubmissionsTab submissions={submissions} onMarkReviewed={markReviewed} />}
            </TabsContent>

            <TabsContent value="investors">
              <InvestorsTab />
            </TabsContent>

            <TabsContent value="documents">
              <DocumentsTab />
            </TabsContent>

            <TabsContent value="nda">
              <NDAAcceptancesTab />
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </main>
  );
}
